import { MAX_ACTIVE_IMAGES_PER_PERSON } from '@sentra/contracts';
import { Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/design/components/alert';
import { Button } from '@/design/components/button';
import { hasRoom } from '@/features/persons/images/accept';
import { type MediaDevicesLike, platformMedia } from '@/features/persons/images/camera';
import type { CaptureCanvas } from '@/features/persons/images/capture';
import type { DeviceStorage } from '@/features/persons/images/devices';
import { stageCapture, type StagedPhotograph, stageFile } from '@/features/persons/images/staged';
import { ImageUpload } from '@/features/persons/images/upload';
import { WebcamCapture } from '@/features/persons/images/webcam';

/**
 * Photographing somebody while registering them, before their record exists.
 *
 * **It is the same two controls the record's panel offers, pointed at memory instead of at a
 * route.** The operator has the person in front of them at exactly this moment and never again as
 * reliably, so asking for the photograph one screen later was asking at the wrong time — and the
 * completeness warning then named a photograph the form structurally could not carry, which taught
 * a room to click past the one warning that matters.
 *
 * **This component holds nothing.** The staged list and the releasing of every preview it minted
 * belong to `person-form.tsx`, which is the thing that knows when the registration is over. Two
 * owners of a `URL.revokeObjectURL` is a preview released twice or, when the form replaces the
 * array without telling this component, never released at all.
 *
 * **It is never mounted beside the record's panel**, which is what keeps `webcam.tsx`'s fixed
 * `id="camera-device"` unambiguous: one is the registration form and the other is the amendment
 * screen, and a person cannot be being registered and amended at once.
 */
export function StagedPhotographs({
  staged,
  onChange,
  busy,
  media = platformMedia(),
  storage = globalThis.localStorage,
  createCanvas,
}: Readonly<{
  staged: readonly StagedPhotograph[];
  onChange: (next: readonly StagedPhotograph[]) => void;
  /** True while the registration is in flight, which is when neither control may add another. */
  busy: boolean;
  media?: MediaDevicesLike | null;
  /** The camera's OWN store, not the ad-hoc autocomplete's: two meanings on one prop is how a
   * test that stubs one silently changes the other. */
  storage?: DeviceStorage;
  createCanvas?: () => CaptureCanvas;
}>): ReactNode {
  const { t } = useTranslation();
  const full = !hasRoom(staged.length);
  // Spread rather than passed as `undefined`, because the prop is optional and
  // `exactOptionalPropertyTypes` distinguishes "absent" from "present and undefined".
  const canvasProp = createCanvas === undefined ? {} : { createCanvas };

  return (
    <section className="flex flex-col gap-4" data-testid="staged-photographs">
      <h3 className="text-lg font-semibold tracking-tight">{t('person.images.title')}</h3>
      <p className="text-sm text-muted-foreground">{t('person.form.photographsHint')}</p>

      {staged.length > 0 && (
        <ul className="flex flex-wrap gap-3">
          {staged.map((one, index) => (
            <li
              key={one.id}
              className="flex flex-col items-center gap-1"
              data-testid="staged-photo"
            >
              <img
                src={one.previewSrc}
                alt={t('person.images.thumbnailAlt', { position: index + 1 })}
                className="size-24 rounded-md border object-cover"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => {
                  onChange(staged.filter((held) => held.id !== one.id));
                }}
              >
                <Trash2 aria-hidden />
                {t('person.images.remove')}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {full && (
        <Alert role="status" data-testid="staged-full">
          <AlertDescription>
            {t('person.images.capReached', { limit: MAX_ACTIVE_IMAGES_PER_PERSON })}
          </AlertDescription>
        </Alert>
      )}
      {!full && (
        <div className="flex flex-col gap-6">
          <ImageUpload
            activeImages={staged.length}
            // **Never `busy`.** `uploading` renders a progress indicator saying the photograph is
            // being uploaded, and while a registration is in flight nothing is: the bytes are still
            // in this browser. The form's own fieldset is what makes the picker inert.
            uploading={false}
            onChoose={(file) => {
              onChange([...staged, stageFile(file)]);
            }}
          />
          <WebcamCapture
            media={media}
            storage={storage}
            {...canvasProp}
            busy={busy}
            onCapture={(frame) => {
              onChange([...staged, stageCapture(frame)]);
            }}
          />
        </div>
      )}
    </section>
  );
}
